/**
 * Hook pour gérer l'authentification admin
 * @module admin/hooks/useAdminAuth
 */

import { useState, useEffect, useCallback } from 'react';
import { verifyAdminAuth, logoutAdmin } from '../services/adminApi';

/**
 * Hook de gestion de l'authentification admin
 * @returns {Object} État et fonctions d'authentification
 */
export const useAdminAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(null);

  /**
   * Vérifie l'authentification au chargement
   */
  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setAuthLoading(false);
      return;
    }

    try {
      const response = await verifyAdminAuth();
      if (response.success) {
        setIsAuthenticated(true);
        setUser(response.user);
      } else {
        localStorage.removeItem('adminToken');
      }
    } catch (err) {
      console.error('Erreur de vérification du token:', err);
      localStorage.removeItem('adminToken');
    } finally {
      setAuthLoading(false);
    }
  }, []);

  /**
   * Gère la connexion réussie
   * @param {Object} userData - Données de l'utilisateur
   */
  const handleLoginSuccess = useCallback((userData) => {
    setIsAuthenticated(true);
    setUser(userData);
  }, []);

  /**
   * Gère la déconnexion
   */
  const handleLogout = useCallback(async () => {
    try {
      await logoutAdmin();
    } catch (err) {
      console.error('Erreur lors de la déconnexion:', err);
    } finally {
      localStorage.removeItem('adminToken');
      setIsAuthenticated(false);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return {
    isAuthenticated,
    authLoading,
    user,
    handleLoginSuccess,
    handleLogout,
  };
};
